import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { concat, Observable, of, timer } from 'rxjs';
import {
  catchError,
  flatMap,
  map,
  mapTo,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { Dashboard } from '../reports/models/dashboard.model';
import { Report } from '../reports/models/report.model';
import {
  ActionsEnum,
  FetchDasboard,
  fetchDashboardError,
  FetchDashboardError,
  fetchDashboardSuccess,
  FetchDashboardSuccess,
  FetchExpandedView,
  fetchExpandedViewSuccess,
  FetchExpandedViewSuccess,
  FetchHomeMetrics,
  fetchHomeMetrics,
  FetchHomeMetricsError,
  fetchHomeMetricsError,
  FetchHomeMetricsSuccess,
  fetchHomeMetricsSuccess,
  FetchReport,
  fetchReportError,
  FetchReportError,
  fetchReportSuccess,
  FetchReportSuccess,
  RegisterHomeMetricsPolling,
  UpdateCurrentReport,
  updateCurrentReport,
  UpdateDashboard,
  updateDashboard,
  UpdateHomeMetrics,
  updateHomeMetrics,
  UpdateReportTypes,
  updateReportTypes,
} from './report-data.actions';
import { ReportDataService } from './report-data.service';
import { HomeMetrics } from './models/home-metrics.model';
import { ExpandedSummary } from './models/expanded-summary.model';
import { List } from 'immutable';

@Injectable()
export class ReportDataEffects {
  constructor(
    private _actions: Actions,
    private _reportDataService: ReportDataService
  ) {}

  @Effect()
  getReport: Observable<
    UpdateCurrentReport | FetchReportSuccess | FetchReportError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_REPORT),
    switchMap(({ payload }: FetchReport) => {
      const { reportType, queryParams } = payload;

      return this._reportDataService
        .getReport(reportType.value, queryParams)
        .pipe(
          flatMap((report: Report) =>
            concat([
              updateCurrentReport(report, reportType),
              fetchReportSuccess(report, reportType),
            ])
          ),
          catchError((error) => of(fetchReportError(error)))
        );
    })
  );

  @Effect()
  getDashboard: Observable<
    | UpdateDashboard
    | UpdateReportTypes
    | FetchDashboardSuccess
    | FetchDashboardError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_DASHBOARD),
    switchMap(({ payload }: FetchDasboard) => {
      return this._reportDataService
        .getDashboard(payload ? payload.params : {})
        .pipe(
          flatMap((dashboard: Dashboard) =>
            concat([
              updateDashboard(dashboard),
              updateReportTypes(dashboard.reportList),
              fetchDashboardSuccess(),
            ])
          ),
          catchError((error) => of(fetchDashboardError(error)))
        );
    })
  );

  @Effect()
  fetchHomeMetrics: Observable<
    UpdateHomeMetrics | FetchHomeMetricsSuccess | FetchHomeMetricsError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_HOME_METRICS),
    switchMap(() => {
      return this._reportDataService.getHomeMetrics().pipe(
        flatMap((homeMetrics: HomeMetrics) =>
          concat([updateHomeMetrics(homeMetrics), fetchHomeMetricsSuccess()])
        ),
        catchError((error) => of(fetchHomeMetricsError(error)))
      );
    })
  );

  @Effect()
  registerHomeMetricsPolling: Observable<FetchHomeMetrics> = this._actions.pipe(
    ofType(ActionsEnum.REGISTER_HOME_METRICS_POLLING),
    switchMap(({ payload }: RegisterHomeMetricsPolling) => {
      const { interval } = payload;

      return timer(0, interval).pipe(
        takeUntil(
          this._actions.pipe(
            ofType(ActionsEnum.UNREGISTER_HOME_METRICS_POLLING)
          )
        ),
        mapTo(fetchHomeMetrics())
      );
    })
  );

  @Effect()
  fetchExpandedView: Observable<
    FetchExpandedViewSuccess | FetchReportError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_EXPANDED_VIEW),
    switchMap(({ payload }: FetchExpandedView) => {
      const { types, currencyCode, topItemsLimit } = payload;
      return this._reportDataService
        .getExpandedView(types, currencyCode, topItemsLimit)
        .pipe(
          map((expandedSummaryList: List<ExpandedSummary>) =>
            fetchExpandedViewSuccess(expandedSummaryList)
          ),
          catchError((error) => of(fetchReportError(error)))
        );
    })
  );
}
